import React, { FC, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Checkbox, Empty, Input, message, Modal, Radio, Select, Spin, Tag } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { RadioChangeEvent } from 'antd/lib/radio';
import {
	ArrowRightOutlined,
	CloseOutlined,
	DeleteOutlined,
	EditOutlined,
	HolderOutlined,
	PushpinFilled,
	PushpinOutlined,
} from '@ant-design/icons';
import ReactDragListView from 'react-drag-listview';
import OWButton from '../ow_button/OWButton';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import useMountEffect from '../../hooks/useMountEffect';
import { getEntityById, isAdminUser, stopDefaultEvents } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { exportTemplatesRestCrudThunks } from '../../thunks/export_templates_thunks';
import {
	ExportSorting,
	fetchExportFields,
	fetchExportPreviewRows,
	queueExport,
} from '../../thunks/export_thunks';
import {
	canEditTemplate,
	columnLabel,
	DEFAULT_FILE_FORMAT,
	defaultColumns,
	ExportColumn,
	ExportField,
	ExportFilters,
	ExportRow,
	ExportTemplate,
	ExportTemplatesState,
	ExportViewer,
	FILE_FORMATS,
	fileFormatSuffix,
	isRenamed,
	matchesSearch,
	moveColumn,
	normalizedColumns,
	renameColumn,
	sameColumns,
	selectAllColumns,
	settleColumnLabel,
	showExportQueued,
	templateColumns,
	toggleColumn,
} from './ExportColumnsUtils';
import SaveExportTemplatePopup from './SaveExportTemplatePopup';
import ExportPreviewModal from './ExportPreviewModal';

const TEMPLATES_TARGET_COLLECTION = 'exportTemplatesDropdown';
const TEMPLATES_SORTING: ExportSorting = { sort_by: 'name', order: 'ascend' };
const NO_TEMPLATE_LABEL = 'No template selected';

const templateIdOf = (template: ExportTemplate) => template.id;

const FILE_FORMAT_OPTIONS = FILE_FORMATS.map((fileFormat) => ({
	label: fileFormat.label,
	value: fileFormat.key,
}));

interface ExportModalProps {
	entityType: string;
	title: string;
	userType: string;
	filters: ExportFilters;
	sorting: ExportSorting;
	onClose: () => void;
	currentUser: ExportViewer;
	exportTemplates: ExportTemplatesState;
	fetchFields: (filters: ExportFilters) => Promise<{ fields: ExportField[]; count: number }>;
	fetchPreviewRows: (filters: ExportFilters, sorting: ExportSorting) => Promise<ExportRow[]>;
	exportColumns: (
		filters: ExportFilters,
		sorting: ExportSorting,
		columns: ExportColumn[],
		fileFormat: string
	) => Promise<string>;
	fetchTemplates: (
		searchText: string,
		targetCollectionName: string,
		pagination: { current: number; pageSize: number },
		sorting: ExportSorting,
		filters: ExportFilters,
		addToTargetCollection: boolean
	) => Promise<unknown>;
	fetchTemplate: (ids: number[]) => Promise<unknown>;
	createTemplate: (template: ExportTemplate) => Promise<ExportTemplate>;
	updateTemplate: (template: ExportTemplate) => Promise<ExportTemplate>;
	deleteTemplate: (template: ExportTemplate) => Promise<unknown>;
}

type TemplatePopup = { template?: ExportTemplate } | null;

// `!`: antd's `.anticon { color: inherit }` loads after Tailwind.
export const pinIconClassName = (userType: string) =>
	userType === ROLE_TYPES.SUPPLIER ? '!text-supplier' : '!text-buyer';

const ExportModal: FC<ExportModalProps> = ({
	entityType,
	title,
	userType,
	filters,
	sorting,
	onClose,
	currentUser,
	exportTemplates,
	fetchFields,
	fetchPreviewRows,
	exportColumns,
	fetchTemplates,
	fetchTemplate,
	createTemplate,
	updateTemplate,
	deleteTemplate,
}) => {
	const [fields, setFields] = useState<ExportField[]>([]);
	const [rowCount, setRowCount] = useState<number>(null);
	const [loadingFields, setLoadingFields] = useState(true);
	const [columns, setColumns] = useState<ExportColumn[]>([]);
	const [fileFormat, setFileFormat] = useState(DEFAULT_FILE_FORMAT);
	const [templatePickerOpen, setTemplatePickerOpen] = useState(true);
	const [selectedTemplateId, setSelectedTemplateId] = useState<number>(null);
	const [searchText, setSearchText] = useState('');
	const [templatePopup, setTemplatePopup] = useState<TemplatePopup>(null);
	const [savingChanges, setSavingChanges] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [preview, setPreview] = useState<{ rows: ExportRow[]; loading: boolean }>(null);
	const [templatePickerKey, setTemplatePickerKey] = useState(0);
	const [pinningTemplateId, setPinningTemplateId] = useState<number>(null);
	const latestPreviewRequest = useRef(0);

	const isAdmin = isAdminUser(currentUser);
	const selectedTemplate: ExportTemplate = getEntityById(selectedTemplateId, exportTemplates, null);
	const baselineColumns = selectedTemplate
		? templateColumns(selectedTemplate, fields)
		: defaultColumns(fields);
	const baselineFileFormat = selectedTemplate ? selectedTemplate.fileFormat : DEFAULT_FILE_FORMAT;
	const isDirty =
		!sameColumns(columns, baselineColumns, fields) || fileFormat !== baselineFileFormat;
	const canEditSelectedTemplate =
		!!selectedTemplate && canEditTemplate(selectedTemplate, currentUser.email, isAdmin);

	useMountEffect(() => {
		fetchFields(filters)
			.then((catalog) => {
				setFields(catalog.fields);
				setRowCount(catalog.count);
				setColumns(defaultColumns(catalog.fields));
			})
			.catch((error) => message.error(error || 'Could not load the export columns'))
			.finally(() => setLoadingFields(false));
	});

	const showTemplatesLoadError = (error: string) =>
		message.error(error || 'Could not load the templates');

	const fetchTemplatePage = (...args: Parameters<ExportModalProps['fetchTemplates']>) =>
		fetchTemplates(...args).catch(showTemplatesLoadError);

	const applyTemplate = (template: ExportTemplate) => {
		setSelectedTemplateId(template ? template.id : null);
		setColumns(template ? templateColumns(template, fields) : defaultColumns(fields));
		setFileFormat(template ? template.fileFormat : DEFAULT_FILE_FORMAT);
		setTemplatePickerOpen(false);
	};

	const handleTemplateChange = (templateId: number) =>
		applyTemplate(getEntityById(templateId, exportTemplates, null));

	const clearTemplate = () => applyTemplate(null);

	// The picker opens with the modal and stays open until the user picks a template or closes it.
	const handleTemplatePickerVisibleChange = (visible: boolean) => {
		if (visible) {
			setTemplatePickerOpen(true);
		}
	};

	const closeTemplatePicker = () => setTemplatePickerOpen(false);

	const openTemplateEditor = (template: ExportTemplate) => (event: React.MouseEvent) => {
		stopDefaultEvents(event);
		applyTemplate(template);
		setTemplatePopup({ template });
	};

	const toggleTemplatePin = (template: ExportTemplate) => () => {
		setPinningTemplateId(template.id);
		updateTemplate({ ...template, isPinned: !template.isPinned })
			.catch((error) => message.error(error || 'Could not update the template'))
			.finally(() => setPinningTemplateId(null));
	};

	const confirmTemplateDelete = (template: ExportTemplate) => () =>
		Modal.confirm({
			title: `Delete “${template.name}”?`,
			okText: 'Delete',
			okButtonProps: { danger: true },
			// Above the template picker, which stays open behind the confirmation.
			zIndex: 1100,
			onOk: () => removeTemplate(template),
		});

	const openNewTemplatePopup = () => {
		setTemplatePickerOpen(false);
		setTemplatePopup({});
	};

	const closeTemplatePopup = () => setTemplatePopup(null);

	const saveTemplate = (name: string, isPublic: boolean) => {
		const editedTemplate = templatePopup && templatePopup.template;
		const templateToSave = {
			...(editedTemplate || {}),
			name,
			isPublic,
			isPinned: editedTemplate ? editedTemplate.isPinned : false,
			entityType,
			columns: normalizedColumns(columns, fields),
			fileFormat,
		};
		const savePromise = editedTemplate
			? updateTemplate(templateToSave)
			: createTemplate(templateToSave);
		return savePromise
			.then((savedTemplate) => {
				setSelectedTemplateId(savedTemplate.id);
				setTemplatePopup(null);
				message.success('Template saved');
			})
			.catch((error) => {
				message.error(error || 'Could not save the template');
				throw error;
			});
	};

	const removeTemplate = (template: ExportTemplate) =>
		deleteTemplate(template)
			.then(() => {
				if (template.id === selectedTemplateId) {
					setSelectedTemplateId(null);
				}
				message.success('Template deleted');
				// Remounting clears the picker's search text and reloads page 1 (the delete leaves its count NaN).
				setTemplatePickerKey((key) => key + 1);
			})
			.catch((error) => {
				message.error(error || 'Could not delete the template');
				throw error;
			});

	const saveChangesToTemplate = () => {
		setSavingChanges(true);
		updateTemplate({ ...selectedTemplate, columns: normalizedColumns(columns, fields), fileFormat })
			.then(() => message.success('Template saved'))
			.catch((error) => message.error(error || 'Could not save the template'))
			.finally(() => setSavingChanges(false));
	};

	const openPreview = () => {
		setTemplatePickerOpen(false);
		latestPreviewRequest.current += 1;
		const previewRequest = latestPreviewRequest.current;
		const isLatestPreview = () => previewRequest === latestPreviewRequest.current;
		setPreview({ rows: [], loading: true });
		fetchPreviewRows(filters, sorting)
			.then((rows) => {
				if (isLatestPreview()) {
					setPreview({ rows, loading: false });
				}
			})
			.catch((error) => {
				if (isLatestPreview()) {
					setPreview(null);
					message.error(error || 'Could not load the preview');
				}
			});
	};

	const closePreview = () => {
		latestPreviewRequest.current += 1;
		setPreview(null);
	};

	const resetToDefault = () => {
		setColumns(baselineColumns);
		setFileFormat(baselineFileFormat);
	};

	const handleFileFormatChange = (event: RadioChangeEvent) => setFileFormat(event.target.value);

	const clearColumns = () => setColumns([]);

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
		setSearchText(event.target.value);

	const handleSelectAllChange = (event: CheckboxChangeEvent) =>
		setColumns(event.target.checked ? selectAllColumns(columns, fields) : []);

	const toggleField = (key: string) => () => setColumns(toggleColumn(columns, key));

	const handleColumnRename = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
		setColumns(renameColumn(columns, key, event.target.value));

	const handleColumnBlur = (key: string) => () => setColumns(settleColumnLabel(columns, key));

	const handleColumnDragEnd = (fromIndex: number, toIndex: number) =>
		setColumns(moveColumn(columns, fromIndex, toIndex));

	const startExport = () => {
		setExporting(true);
		exportColumns(filters, sorting, normalizedColumns(columns, fields), fileFormat)
			.then((queuedMessage) => {
				showExportQueued(queuedMessage);
				onClose();
			})
			.catch((error) => {
				setExporting(false);
				message.error(error || 'Could not start the export');
			});
	};

	const allSelected = fields.length > 0 && columns.length === fields.length;
	const visibleFields = fields.filter((field) =>
		matchesSearch(
			field,
			columns.find((column) => column.key === field.key),
			searchText
		)
	);

	const renderTemplateOption = (template: ExportTemplate) => {
		const isAuthor = canEditTemplate(template, currentUser.email, isAdmin);
		const pinClassName = isAuthor ? pinIconClassName(userType) : undefined;
		return (
			<Select.Option key={template.id} value={template.id}>
				<div className="flex items-center justify-between gap-3">
					<div className="flex min-w-0 items-center gap-2">
						<Tag className="m-0">{template.isPublic ? 'Your org' : 'You'}</Tag>
						<span className="truncate">{template.name}</span>
						<span className="text-character-secondary">
							{fileFormatSuffix(template.fileFormat)}
						</span>
					</div>
					<div className="flex items-center gap-3" onClick={stopDefaultEvents}>
						{isAuthor ? (
							<>
								<EditOutlined className="!text-grey-7" onClick={openTemplateEditor(template)} />
								<DeleteOutlined
									className="!text-grey-7"
									onClick={confirmTemplateDelete(template)}
								/>
							</>
						) : null}
						<OWButton
							disabled={!isAuthor}
							loading={pinningTemplateId === template.id}
							icon={
								template.isPinned ? (
									<PushpinFilled className={pinClassName} />
								) : (
									<PushpinOutlined className={pinClassName} />
								)
							}
							onClick={toggleTemplatePin(template)}
						/>
					</div>
				</div>
			</Select.Option>
		);
	};

	const renderTemplatePicker = (menu: React.ReactNode) => (
		<>
			<div className="flex items-center justify-between gap-2 bg-grey-3 px-3 py-2 text-character-secondary">
				<span>
					<PushpinFilled className={`mr-2 ${pinIconClassName(userType)}`} />
					Pinned templates are shown in the Export dropdown
				</span>
				<CloseOutlined className="cursor-pointer" onClick={closeTemplatePicker} />
			</div>
			<div
				className={`bodymedium cursor-pointer px-3 py-2 ${selectedTemplate ? '' : 'bg-purple-1'}`}
				onClick={clearTemplate}
			>
				{NO_TEMPLATE_LABEL}
			</div>
			{menu}
		</>
	);

	const modalTitle = (
		<div className="flex items-center justify-between gap-4 pr-8">
			<div className="flex items-baseline gap-3">
				<span className="h5medium">{title}</span>
				{rowCount !== null ? (
					<span className="bodyregular text-character-secondary">
						{rowCount} {rowCount === 1 ? 'row' : 'rows'}
					</span>
				) : null}
			</div>
			<OWAsyncSelect
				key={templatePickerKey}
				stateSlice={exportTemplates}
				targetCollectionName={TEMPLATES_TARGET_COLLECTION}
				sortBy={TEMPLATES_SORTING}
				fetchData={fetchTemplatePage}
				fetchMultiple={fetchTemplate}
				renderRecord={renderTemplateOption}
				valueAccessor={templateIdOf}
				value={selectedTemplateId}
				onChange={handleTemplateChange}
				placeholder={NO_TEMPLATE_LABEL}
				disabledPlaceholder={NO_TEMPLATE_LABEL}
				disabled={loadingFields}
				dropdownRender={renderTemplatePicker}
				selectProps={{
					className: 'w-72',
					dropdownMatchSelectWidth: 520,
					open: templatePickerOpen,
					onDropdownVisibleChange: handleTemplatePickerVisibleChange,
				}}
			/>
		</div>
	);

	const exportButton = (
		<OWButton
			type="primary"
			disabled={columns.length === 0}
			loading={exporting}
			onClick={startExport}
		>
			Export
		</OWButton>
	);

	const footer = (
		<div className="flex items-center justify-between">
			<div className="flex items-center gap-2">
				{selectedTemplate && isDirty && canEditSelectedTemplate ? (
					<OWButton
						disabled={columns.length === 0}
						loading={savingChanges}
						onClick={saveChangesToTemplate}
					>
						Save changes
					</OWButton>
				) : null}
				<Button disabled={columns.length === 0} onClick={openNewTemplatePopup}>
					{selectedTemplate ? 'Save as new template' : 'Save new template'}
				</Button>
				<Button type="link" disabled={!isDirty} onClick={resetToDefault}>
					Reset to default
				</Button>
			</div>
			<div className="flex items-center gap-2">
				<Button disabled={columns.length === 0} onClick={openPreview}>
					Preview
				</Button>
				{exportButton}
			</div>
		</div>
	);

	return (
		<Modal visible={true} width={820} title={modalTitle} footer={footer} onCancel={onClose}>
			<Spin spinning={loadingFields}>
				<div className="flex h-[480px]">
					<div className="flex w-1/2 flex-col border-0 border-r border-solid border-grey-4 pr-4">
						<Radio.Group
							className="!mb-3"
							optionType="button"
							options={FILE_FORMAT_OPTIONS}
							value={fileFormat}
							onChange={handleFileFormatChange}
						/>
						<Input.Search
							allowClear
							placeholder="Search"
							value={searchText}
							onChange={handleSearchChange}
						/>
						<div className="mt-3 flex items-center justify-between border-0 border-b border-solid border-grey-4 pb-2">
							<Checkbox checked={allSelected} onChange={handleSelectAllChange}>
								<span className="bodyregular uppercase text-character-secondary">
									All fields ({columns.length}/{fields.length})
								</span>
							</Checkbox>
							<Button type="link" className="px-0" onClick={clearColumns}>
								Clear all
							</Button>
						</div>
						<div className="flex-1 overflow-y-auto">
							{visibleFields.map((field) => {
								const column = columns.find((candidate) => candidate.key === field.key);
								return (
									<div key={field.key} className="py-2">
										<Checkbox checked={!!column} onChange={toggleField(field.key)}>
											<span className="bodyregular">
												{field.label}
												{column && isRenamed(column, fields) ? (
													<>
														<ArrowRightOutlined className="mx-2 text-grey-7" />
														{column.label.trim()}
													</>
												) : null}
											</span>
										</Checkbox>
									</div>
								);
							})}
						</div>
					</div>
					<div className="flex w-1/2 flex-col pl-4">
						<div className="bodyregular border-0 border-b border-solid border-grey-4 pb-2 uppercase text-character-secondary">
							Column order
						</div>
						<div className="flex-1 overflow-y-auto pt-2">
							{columns.length === 0 && !loadingFields ? (
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description="Select the fields to export"
								/>
							) : (
								/* @ts-ignore - react-drag-listview type definitions are incomplete */
								<ReactDragListView
									nodeSelector=".export-column-row"
									handleSelector=".export-column-handle"
									onDragEnd={handleColumnDragEnd}
								>
									{columns.map((column) => (
										<div key={column.key} className="export-column-row py-1">
											<div className="flex items-center gap-2">
												<HolderOutlined className="export-column-handle cursor-move text-grey-6" />
												<Input
													value={columnLabel(column, fields)}
													onChange={handleColumnRename(column.key)}
													onBlur={handleColumnBlur(column.key)}
												/>
												<CloseOutlined
													className="cursor-pointer text-grey-7"
													onClick={toggleField(column.key)}
												/>
											</div>
											{isRenamed(column, fields) ? (
												<div className="footnoteregular ml-6 mt-1 text-character-secondary">
													Changed from “{columnLabel({ key: column.key }, fields)}”
												</div>
											) : null}
										</div>
									))}
								</ReactDragListView>
							)}
						</div>
					</div>
				</div>
			</Spin>
			{templatePopup ? (
				<SaveExportTemplatePopup
					template={templatePopup.template}
					isAdmin={isAdmin}
					onSave={saveTemplate}
					onCancel={closeTemplatePopup}
				/>
			) : null}
			{preview ? (
				<ExportPreviewModal
					columns={normalizedColumns(columns, fields)}
					fields={fields}
					rows={preview.rows}
					rowCount={rowCount}
					loading={preview.loading}
					exportButton={exportButton}
					onClose={closePreview}
				/>
			) : null}
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	exportTemplates: state.export_templates,
});

type ExportModalDispatchProps = Pick<
	ExportModalProps,
	| 'fetchFields'
	| 'fetchPreviewRows'
	| 'exportColumns'
	| 'fetchTemplates'
	| 'fetchTemplate'
	| 'createTemplate'
	| 'updateTemplate'
	| 'deleteTemplate'
>;

const mapDispatchToProps = (
	dispatch,
	ownProps: Pick<ExportModalProps, 'userType' | 'entityType'>
): ExportModalDispatchProps => {
	const templateThunks = exportTemplatesRestCrudThunks(ownProps.userType);
	return {
		fetchFields: (filters) =>
			dispatch(fetchExportFields(ownProps.userType, ownProps.entityType)(filters)),
		fetchPreviewRows: (filters, sorting) =>
			dispatch(fetchExportPreviewRows(ownProps.userType, ownProps.entityType)(filters, sorting)),
		exportColumns: (filters, sorting, columns, fileFormat) =>
			dispatch(
				queueExport(ownProps.userType, ownProps.entityType)(filters, sorting, columns, fileFormat)
			),
		fetchTemplates: (
			searchText,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			addToTargetCollection
		) =>
			dispatch(
				templateThunks.read(
					{ entityType: ownProps.entityType, ...(searchText ? { name: searchText } : {}) },
					targetCollectionName,
					pagination,
					sorting,
					filters,
					addToTargetCollection
				)
			),
		fetchTemplate: (ids) => dispatch(templateThunks.readOne(ids[0])),
		createTemplate: (template) =>
			dispatch(templateThunks.create(template, 'id', TEMPLATES_TARGET_COLLECTION)),
		updateTemplate: (template) => dispatch(templateThunks.update(template)),
		deleteTemplate: (template) => dispatch(templateThunks.deleteImmediate(template)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(ExportModal);
